import React from 'react';
import {
  TableContainer,
  Paper,
  TableHead,
  Checkbox,
  TextField,
  useTheme,
  Table,
  TableRow,
  TableCell,
  TableBody,
  CircularProgress,
  Box,
  Collapse,
  IconButton,
  Stack
} from '@mui/material';
import FilterNoneIcon from '@mui/icons-material/FilterNone';
import LibraryAddCheckIcon from '@mui/icons-material/LibraryAddCheck';
import IndeterminateCheckBoxIcon from '@mui/icons-material/IndeterminateCheckBox';
import Empty from '../Empty';
import {
  IEcommerceProductHierarchy,
  IEcommerceProductHierarchyItem,
  IEcommerceSubBrandHierarchy
} from 'src/models/ecommerce';
import ProductPostCard from '../ProductPostCard';
import { useState } from 'react';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
interface TableProps {
  data: IEcommerceProductHierarchy[];
  loading: boolean;
  searchValue: string;
  handleChangeSearchValue: React.ChangeEventHandler<
    HTMLTextAreaElement | HTMLInputElement
  >;
  selectedProducts: IEcommerceProductHierarchyItem[];
  onSelectedProductsChange?: (
    products: IEcommerceProductHierarchyItem[]
  ) => void;
}

const TableCommentingToProduct: React.FC<TableProps> = ({
  data,
  loading,
  searchValue,
  handleChangeSearchValue,
  selectedProducts,
  onSelectedProductsChange
}) => {
  const theme = useTheme();

  // Helper function to find product by id and originalId
  const isProductSelected = (product: IEcommerceProductHierarchyItem) => {
    return selectedProducts.some(
      (p) => p.id === product.id && p.originalId === product.originalId
    );
  };

  // Helper function to get all products from a subbrand
  const getSubbrandProducts = (subbrand: IEcommerceSubBrandHierarchy) => {
    return subbrand.products || [];
  };

  // Helper function to get all products from a brand
  const getBrandProducts = (brand: IEcommerceProductHierarchy) => {
    const products: IEcommerceProductHierarchyItem[] = [];
    brand.subBrands?.forEach((subbrand) => {
      products.push(...getSubbrandProducts(subbrand));
    });
    return products;
  };

  // Check if all products in a subbrand are selected
  const isSubbrandFullySelected = (subbrand: IEcommerceSubBrandHierarchy) => {
    const products = getSubbrandProducts(subbrand);
    if (products.length === 0) return false;
    return products.every((p) => isProductSelected(p));
  };

  // Check if some (but not all) products in a subbrand are selected
  const isSubbrandIndeterminate = (subbrand: IEcommerceSubBrandHierarchy) => {
    const products = getSubbrandProducts(subbrand);
    if (products.length === 0) return false;
    const selectedCount = products.filter((p) => isProductSelected(p)).length;
    return selectedCount > 0 && selectedCount < products.length;
  };

  // Check if all products in a brand are selected
  const isBrandFullySelected = (brand: IEcommerceProductHierarchy) => {
    const products = getBrandProducts(brand);
    if (products.length === 0) return false;
    return products.every((p) => isProductSelected(p));
  };

  // Check if some (but not all) products in a brand are selected
  const isBrandIndeterminate = (brand: IEcommerceProductHierarchy) => {
    const products = getBrandProducts(brand);
    if (products.length === 0) return false;
    const selectedCount = products.filter((p) => isProductSelected(p)).length;
    return selectedCount > 0 && selectedCount < products.length;
  };

  // Handle product checkbox change
  const handleProductCheckChange = (
    product: IEcommerceProductHierarchyItem,
    checked: boolean
  ) => {
    let updatedProducts: IEcommerceProductHierarchyItem[];

    if (checked) {
      // Add product if not already selected
      if (!isProductSelected(product)) {
        updatedProducts = [...selectedProducts, product];
      } else {
        updatedProducts = selectedProducts;
      }
    } else {
      // Remove product
      updatedProducts = selectedProducts.filter(
        (p) => !(p.id === product.id && p.originalId === product.originalId)
      );
    }

    onSelectedProductsChange?.(updatedProducts);
  };

  // Handle subbrand checkbox change
  const handleSubbrandCheckChange = (
    subbrand: IEcommerceSubBrandHierarchy,
    checked: boolean
  ) => {
    const subbrandProducts = getSubbrandProducts(subbrand);
    let updatedProducts = [...selectedProducts];

    if (checked) {
      // Add all products from subbrand
      subbrandProducts.forEach((product) => {
        if (!isProductSelected(product)) {
          updatedProducts.push(product);
        }
      });
    } else {
      // Remove all products from subbrand
      updatedProducts = updatedProducts.filter(
        (selected) =>
          !subbrandProducts.some(
            (p) => p.id === selected.id && p.originalId === selected.originalId
          )
      );
    }

    onSelectedProductsChange?.(updatedProducts);
  };

  // Handle brand checkbox change
  const handleBrandCheckChange = (
    brand: IEcommerceProductHierarchy,
    checked: boolean
  ) => {
    const brandProducts = getBrandProducts(brand);
    let updatedProducts = [...selectedProducts];

    if (checked) {
      // Add all products from brand
      brandProducts.forEach((product) => {
        if (!isProductSelected(product)) {
          updatedProducts.push(product);
        }
      });
    } else {
      // Remove all products from brand
      updatedProducts = updatedProducts.filter(
        (selected) =>
          !brandProducts.some(
            (p) => p.id === selected.id && p.originalId === selected.originalId
          )
      );
    }

    onSelectedProductsChange?.(updatedProducts);
  };

  // Get all products from all brands
  const getAllProducts = () => {
    const allProducts: IEcommerceProductHierarchyItem[] = [];
    data.forEach((brand) => {
      allProducts.push(...getBrandProducts(brand));
    });
    return allProducts;
  };

  // Check if all products are selected
  const isAllProductsSelected = () => {
    const allProducts = getAllProducts();
    if (allProducts.length === 0) return false;
    return allProducts.every((p) => isProductSelected(p));
  };

  // Check if some (but not all) products are selected
  const isSelectAllIndeterminate = () => {
    const allProducts = getAllProducts();
    if (allProducts.length === 0) return false;
    const selectedCount = allProducts.filter((p) =>
      isProductSelected(p)
    ).length;
    return selectedCount > 0 && selectedCount < allProducts.length;
  };

  // Handle select all checkbox change
  const handleSelectAllChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (event.target.checked) {
      // Select all products
      const allProducts = getAllProducts();
      onSelectedProductsChange?.(allProducts);
    } else {
      // Clear all selections
      onSelectedProductsChange?.([]);
    }
  };

  return (
    <TableContainer
      component={Paper}
      sx={{
        mt: theme.spacing(2),
        maxHeight: 480,
        marginTop: 0
      }}
    >
      <Table stickyHeader>
        <TableHead
          sx={{
            background: theme.palette.primary.main
          }}
        >
          <TableRow>
            <TableCell padding="checkbox" sx={{ width: '3%' }}>
              <Checkbox
                checked={isAllProductsSelected()}
                indeterminate={isSelectAllIndeterminate()}
                size="small"
                onChange={handleSelectAllChange}
                icon={
                  <FilterNoneIcon
                    sx={{
                      color: theme.palette.common.white,
                      width: theme.spacing(2.5),
                      height: theme.spacing(2.5)
                    }}
                  />
                }
                checkedIcon={
                  <LibraryAddCheckIcon
                    sx={{
                      color: theme.palette.common.white,
                      width: theme.spacing(2.5),
                      height: theme.spacing(2.5)
                    }}
                  />
                }
                indeterminateIcon={
                  <IndeterminateCheckBoxIcon
                    sx={{
                      color: theme.palette.common.white,
                      width: theme.spacing(2.5),
                      height: theme.spacing(2.5)
                    }}
                  />
                }
              />
            </TableCell>
            <TableCell
              size="small"
              padding="none"
              sx={{
                color: 'white',
                fontWeight: 600,
                flex: 1
              }}
            >
              Name
            </TableCell>
            <TableCell
              size="small"
              // align="center"
              padding="none"
              sx={{
                color: 'white',
                fontWeight: 600,
                width: '200px'
              }}
            >
              Category
            </TableCell>
            <TableCell
              size="small"
              align="center"
              padding="none"
              sx={{
                color: 'white',
                fontWeight: 400,
                width: '300px'
              }}
            >
              <TextField
                value={searchValue}
                onChange={handleChangeSearchValue}
                variant="outlined"
                placeholder="Search"
                size="small"
              />
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {loading ? (
            <TableRow hover={false}>
              <TableCell align="center" colSpan={4}>
                <Box
                  sx={{
                    height: 300,
                    background: theme.palette.grey[200],
                    width: '100%',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    backgroundColor: theme.palette.grey[200],
                    opacity: '0.3'
                  }}
                >
                  <CircularProgress disableShrink color="primary" size={64} />
                </Box>
              </TableCell>
            </TableRow>
          ) : data.length < 0 ? (
            <TableRow hover={false}>
              <TableCell align="center" colSpan={4}>
                <Box
                  sx={{
                    height: 300,
                    width: '100%',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center'
                  }}
                >
                  <Empty message="There's no data yet" />
                </Box>
              </TableCell>
            </TableRow>
          ) : (
            data.map((row) => (
              <SubBrandListRow
                row={row}
                key={row.id}
                selectedProducts={selectedProducts}
                isProductSelected={isProductSelected}
                isSubbrandFullySelected={isSubbrandFullySelected}
                isSubbrandIndeterminate={isSubbrandIndeterminate}
                isBrandFullySelected={isBrandFullySelected}
                isBrandIndeterminate={isBrandIndeterminate}
                onProductCheckChange={handleProductCheckChange}
                onSubbrandCheckChange={handleSubbrandCheckChange}
                onBrandCheckChange={handleBrandCheckChange}
              />
            ))
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default TableCommentingToProduct;

function SubBrandListRow(props: {
  row: IEcommerceProductHierarchy;
  selectedProducts: IEcommerceProductHierarchyItem[];
  isProductSelected: (product: IEcommerceProductHierarchyItem) => boolean;
  isSubbrandFullySelected: (subbrand: IEcommerceSubBrandHierarchy) => boolean;
  isSubbrandIndeterminate: (subbrand: IEcommerceSubBrandHierarchy) => boolean;
  isBrandFullySelected: (brand: IEcommerceProductHierarchy) => boolean;
  isBrandIndeterminate: (brand: IEcommerceProductHierarchy) => boolean;
  onProductCheckChange: (
    product: IEcommerceProductHierarchyItem,
    checked: boolean
  ) => void;
  onSubbrandCheckChange: (
    subbrand: IEcommerceSubBrandHierarchy,
    checked: boolean
  ) => void;
  onBrandCheckChange: (
    brand: IEcommerceProductHierarchy,
    checked: boolean
  ) => void;
}) {
  const { row } = props;
  const [open, setOpen] = useState(false);
  const isBrandSelected = props.isBrandFullySelected(row);
  const isBrandIndeterminate = props.isBrandIndeterminate(row);

  const handleBrandCheckboxChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    props.onBrandCheckChange(row, event.target.checked);
  };

  return (
    <React.Fragment>
      <TableRow
        sx={{
          '&:last-child td, &:last-child th': {
            border: 0
          }
        }}
      >
        <TableCell padding="checkbox" sx={{ width: '3%' }}>
          <Stack direction="row" gap={1}>
            <Checkbox
              color="primary"
              checked={isBrandSelected}
              indeterminate={isBrandIndeterminate}
              onChange={handleBrandCheckboxChange}
            />
            <IconButton size="small" onClick={() => setOpen(!open)}>
              {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
            </IconButton>
          </Stack>
        </TableCell>
        <TableCell padding="none" sx={{ flex: 1 }}>
          {row.name}
        </TableCell>
        <TableCell colSpan={2} padding="none" sx={{ width: '200px' }}>
          {row.category}
        </TableCell>
      </TableRow>
      {/* Sub brand list */}
      <TableRow
        sx={{
          '&:last-child td, &:last-child th': {
            border: 0
          }
        }}
      >
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={4}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            {/* <Box sx={{ margin: 1 }}> */}
            <Table size="small" aria-label="purchases">
              <TableBody>
                {row.subBrands.map((subBrand) => (
                  <ProductListRow
                    key={subBrand.id}
                    row={subBrand}
                    selectedProducts={props.selectedProducts}
                    isProductSelected={props.isProductSelected}
                    isSubbrandFullySelected={props.isSubbrandFullySelected}
                    isSubbrandIndeterminate={props.isSubbrandIndeterminate}
                    onProductCheckChange={props.onProductCheckChange}
                    onSubbrandCheckChange={props.onSubbrandCheckChange}
                  />
                ))}
              </TableBody>
            </Table>
          </Collapse>
        </TableCell>
      </TableRow>
    </React.Fragment>
  );
}

function ProductListRow(props: {
  row: IEcommerceSubBrandHierarchy;
  selectedProducts: IEcommerceProductHierarchyItem[];
  isProductSelected: (product: IEcommerceProductHierarchyItem) => boolean;
  isSubbrandFullySelected: (subbrand: IEcommerceSubBrandHierarchy) => boolean;
  isSubbrandIndeterminate: (subbrand: IEcommerceSubBrandHierarchy) => boolean;
  onProductCheckChange: (
    product: IEcommerceProductHierarchyItem,
    checked: boolean
  ) => void;
  onSubbrandCheckChange: (
    subbrand: IEcommerceSubBrandHierarchy,
    checked: boolean
  ) => void;
}) {
  const { row } = props;
  const [open, setOpen] = useState(false);
  const isSubbrandSelected = props.isSubbrandFullySelected(row);
  const isSubbrandIndeterminate = props.isSubbrandIndeterminate(row);

  const handleSubbrandCheckboxChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    props.onSubbrandCheckChange(row, event.target.checked);
  };

  return (
    <React.Fragment>
      <TableRow
        sx={{
          '&:last-child td, &:last-child th': {
            border: 0
          }
        }}
      >
        <TableCell padding="checkbox" sx={{ width: '3%' }}>
          <Stack direction="row" gap={1}>
            <Checkbox
              color="primary"
              checked={isSubbrandSelected}
              indeterminate={isSubbrandIndeterminate}
              onChange={handleSubbrandCheckboxChange}
            />
            <IconButton size="small" onClick={() => setOpen(!open)}>
              {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
            </IconButton>
          </Stack>
        </TableCell>
        <TableCell padding="none" sx={{ flex: 1, minWidth: '104px' }}>
          {row.name}
        </TableCell>
        <TableCell colSpan={2} padding="none" sx={{ width: '200px' }}>
          {row.category}
        </TableCell>
      </TableRow>
      {/* Sub brand list */}
      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={4}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Table size="small" aria-label="purchases">
              <TableBody>
                {row.products.map((product) => (
                  <ProductCheckRow
                    key={product.id}
                    product={product}
                    isSelected={props.isProductSelected(product)}
                    onCheckChange={props.onProductCheckChange}
                  />
                ))}
              </TableBody>
            </Table>
          </Collapse>
        </TableCell>
      </TableRow>
    </React.Fragment>
  );
}

function ProductCheckRow(props: {
  product: IEcommerceProductHierarchyItem;
  isSelected: boolean;
  onCheckChange: (
    product: IEcommerceProductHierarchyItem,
    checked: boolean
  ) => void;
}) {
  const { product, isSelected, onCheckChange } = props;

  const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onCheckChange(product, event.target.checked);
  };

  return (
    <TableRow
      key={product.id}
      sx={{
        '&:last-child td, &:last-child th': {
          border: 0
        }
      }}
    >
      <TableCell padding="checkbox" sx={{ width: '3%' }}>
        <Stack direction="row" gap={1}>
          <Checkbox
            color="primary"
            checked={isSelected}
            onChange={handleCheckboxChange}
          />
        </Stack>
      </TableCell>
      <TableCell padding="none" sx={{ flex: 1 }} colSpan={2}>
        <ProductPostCard
          title={product.name}
          imageUrl={product.image}
          productLink={product.link}
        />
      </TableCell>
    </TableRow>
  );
}
